/**
 * Shared SOW / Execution Plan upload (SAD-898): upload to the program's zou
 * project, record a ProgramDocument, ProcessBatch with document_kind=sow
 * (and an optional named workstream), and register the processing tray job
 * that lands on Modifications / ContractIntake review.
 *
 * Docs (DocumentsTab) and the Teams surfaces (New team drawer, team page)
 * all go through this — no second ProcessBatch path.
 */ import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUploadProjectFiles } from '../../../hooks/useProjectFiles';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { useProcessingJobsStore } from '../../../lib/processing/jobsStore';
import { parseUtcDate } from '../_shared/api/nullable';
import { SOW_DOCUMENT_CATEGORY } from '../_shared/constants/documentCategories';
import { DOC_KEY, useCreateProgramDocument } from './hooks';
export function sowModsReviewLink(programId) {
    return `/delivery/programs/${programId}/docs?tab=modifications`;
}
/** Toast after the document is recorded and ProcessBatch(sow) is kicked off. */ export const SOW_INTAKE_STARTED_TOAST = 'SOW / Execution Plan recorded — Specra is reading it now (watch the processing tray). When the read finishes, any open questions (which team, assignments) appear on the Modifications tab.';
/**
 * Thrown when the ProgramDocument was recorded but ProcessBatch (sow) failed
 * to start. Callers that already toast for upload failures should suppress
 * their generic toast for this error — the hook already surfaced intake.
 */ export class SowIntakeStartError extends Error {
    constructor(message){
        super(message);
        this.name = 'SowIntakeStartError';
    }
}
/** Kick off SOW intake for an attachment already on the zou project. */ export async function startSowIntake(opts) {
    const { programId, zouProjectId, attachmentId, workstreamId } = opts;
    const batch = await proposalProjectApiConnect.processBatch(zouProjectId, [
        attachmentId
    ], {
        documentKind: 'sow',
        ...workstreamId ? {
            workstreamId
        } : {}
    });
    useProcessingJobsStore.getState().registerJob({
        batchId: batch.batchId,
        zouProjectId,
        kind: 'contract-docs',
        label: 'Reading SOW / Execution Plan',
        link: sowModsReviewLink(programId)
    });
    return {
        batchId: batch.batchId
    };
}
function todayUtcYmd() {
    return new Date().toISOString().slice(0, 10);
}
/**
 * Upload a SOW / Execution Plan file for a program (and optional team):
 * zou project file → ProgramDocument (SOW category) → ProcessBatch sow.
 */ export function useUploadSowDocument() {
    const uploadProjectFiles = useUploadProjectFiles();
    const create = useCreateProgramDocument();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const { programId, zouProjectId, file, workstreamId } = input;
            if (!zouProjectId) {
                throw new Error('This program has no linked contract project to upload against.');
            }
            const uploaded = await uploadProjectFiles.mutateAsync({
                projectId: zouProjectId,
                files: [
                    file
                ]
            });
            if (uploaded.failed.length > 0) {
                throw new Error(uploaded.failed.map((f)=>`${f.name}: ${f.error}`).join('; '));
            }
            const attachmentId = uploaded.files[0]?.attachmentId ?? '';
            if (!attachmentId) {
                throw new Error('Upload did not return a file id — the document was not recorded');
            }
            const title = input.title?.trim() || file.name.replace(/\.[^.]+$/, '') || 'SOW / Execution Plan';
            await create.mutateAsync({
                programId,
                title,
                description: '',
                docDate: parseUtcDate(input.docDateYmd ?? todayUtcYmd()),
                fileId: attachmentId,
                fileName: '',
                category: SOW_DOCUMENT_CATEGORY,
                uploadedBy: ''
            });
            try {
                const { batchId } = await startSowIntake({
                    programId,
                    zouProjectId,
                    attachmentId,
                    workstreamId
                });
                toast.success(SOW_INTAKE_STARTED_TOAST);
                void qc.invalidateQueries({
                    queryKey: [
                        DOC_KEY
                    ]
                });
                return {
                    batchId,
                    attachmentId
                };
            } catch (err) {
                const message = `Document recorded, but SOW intake failed to start: ${err instanceof Error ? err.message : 'unknown error'}`;
                toast.error(message);
                throw new SowIntakeStartError(message);
            }
        }
    });
}
